import React, { Fragment } from 'react';

import AnimatedProgressCircle from './AnimatedProgressCircle';
import UsageGraph from './UsageGraph';
import Module, { StatTextLine, StatTextValue } from './Module';
import { TIERS_MAP } from './constants';

import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import type {
  PartnerDashboard,
  PartnerTiersType,
  Statistics,
} from 'app/developer-v3/types/partnerDashboard';
import { Link, Spacer, Text } from '@zapier/design-system';

// If the partner has enough users to progress to the next level
function UpgradeStatLine({ currentActive, hasNextTier, stats }) {
  return (
    hasNextTier &&
    stats.upgrade != null &&
    currentActive <= stats.upgrade && (
      <StatTextLine>
        Get to <StatTextValue>{stats.upgrade.toLocaleString()}</StatTextValue>{' '}
        to move to the next level.
      </StatTextLine>
    )
  );
}

// If the partner has enough users to maintain their current level
function MaintainTierStatLine({ currentActive, hasPrevTier, stats }) {
  return (
    hasPrevTier &&
    stats.minimum != null &&
    currentActive <= stats.minimum && (
      <StatTextLine>
        Maintain current level{' '}
        <StatTextValue>{stats.minimum.toLocaleString()}</StatTextValue>
      </StatTextLine>
    )
  );
}

// Congrats for reaching their next level (or maintaining top level)
function CongratsStatLine({ currentActive, hasNextTier, stats, tierMeta }) {
  const congratsText = hasNextTier
    ? 'for the next level'
    : `to maintain ${tierMeta.label}`;
  const congratsCompare = hasNextTier ? stats.upgrade : stats.minimum;

  return (
    congratsCompare != null &&
    currentActive >= congratsCompare && (
      <StatTextLine key="congrats">
        Nice work! You have enough active users {congratsText}.
      </StatTextLine>
    )
  );
}

function StatLines({ partnerTier, usage: { currentActive, tier: stats } }) {
  // TODO: Remove this check when BETA is an offical TIER
  const tierMeta = TIERS_MAP[partnerTier] || TIERS_MAP.BETA;
  const props = {
    currentActive,
    hasNextTier: !!tierMeta.next,
    hasPrevTier: !!tierMeta.prev,
    stats,
    tierMeta,
  };
  return (
    <Fragment>
      <UpgradeStatLine {...props} />
      <MaintainTierStatLine {...props} />
      <CongratsStatLine {...props} />
    </Fragment>
  );
}

type Props = {
  isBeta?: boolean;
  partnerTier: PartnerTiersType;
  usage: {
    currentActive: PartnerDashboard['usage']['currentActive'];
    hasCompleted: PartnerDashboard['usage']['hasCompleted'];
    statistics: Statistics;
    tier: PartnerDashboard['usage']['tier'];
  };
};

export default function ActiveUsers({ isBeta, partnerTier, usage }: Props) {
  return (
    <Module
      figure={
        <AnimatedProgressCircle
          height={isBeta ? undefined : 80}
          initValue={usage.tier.percent * 100}
        />
      }
      help="This metric shows how many people have at least 1 Zap that has generated a successful task with your Zapier integration in the past 90 days."
      isBeta={isBeta}
      stats={{
        title: 'Active users in the past 90 days.',
        current: usage.currentActive.toLocaleString(),
        lines: <StatLines partnerTier={partnerTier} usage={usage} />,
      }}
      title="Active users"
    >
      <UsageGraph isBeta={isBeta} statistics={usage.statistics} />
      {isBeta && <Spacer height={20} />}
      <Text>
        Use this{' '}
        <Link
          href={`${BASE_DOC_URL_VB}/publish/partner-program-tips`}
          target="_blank"
        >
          list of ideas{' '}
        </Link>
        to increase your active users.
      </Text>
    </Module>
  );
}
